import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Video, VideoOverlayObject, Coordinate } from './VideoTypes';
import { timeSpanToSeconds } from '../../services/sessionModels';
import VideoView from './VideoView';
import useInterval from '../Util/useInterval';
import { ProgressState } from './VideoPlayer';
import { ClickInfo, useClickHandler, MouseButton } from '../Sessions/Label/useClickHandler';
import { makeStyles } from '@material-ui/core';
import ToggleMenuItem from './Menu/ToggleMenuItem';
import ActionableMenuItem from './Menu/ActionableMenuItem';
import Menu from './Menu/Menu';
import useLocalStorage from '../Util/useLocalStorage';

import NavigateLeftIcon from '@material-ui/icons/NavigateBefore';
import NavigateRightIcon from '@material-ui/icons/NavigateNext';
import { useContainerDimensions } from './useContainerDimensions';
import { ReactZoomPanPinchState } from 'react-zoom-pan-pinch';

const syncOffsetMs = (video: Video) => timeSpanToSeconds(video.syncOffset) * 1000;

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  bottomTextOverlay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    bottom: 0,
  },
  centerTextOverlay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    backgroundColor: '#0009',
    color: '#fff',
    transform: 'translateY(-100px)',
    minWidth: '50px',
    padding: '2px 4px',
  },
  menuContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    backgroundColor: '#000a',
    borderBottom: '1px dashed #ff5a0f66',
  },
}));

export enum PanMethod {
  Drag,
  Edge,
  Center,
}

export type TextOverlay = {
  frame_id?: number;
  value: string;
  color?: string;
  background_color?: string;
};

type PopupMessage = {
  value: string;
  priority: number;
  time?: number;
};

type Props = {
  videos: Video[];
  showOverlayUI: boolean;
  saveId?: string;
  defaultPanMethod?: PanMethod;
  updateIntervalMs?: number;
  videoPlaying?: boolean;
  onChangeVideoPlaying?: (newPlayingState: boolean, logReason: string) => void;
  playTime: number;
  playTimeChanged?: (newTime: number) => void;
  seekVideo?: boolean;
  setSeekVideo?: React.Dispatch<React.SetStateAction<boolean>>;
  playbackSpeed?: number;
  viewOverlays?: Map<string, VideoOverlayObject[]>;
  mouseOverlay?: VideoOverlayObject[];
  onViewMouseDown?: (id: string, info: ClickInfo) => void;
  onViewMouseUp?: (id: string, info: ClickInfo) => void;
  onViewMouseMove?: (id: string, coord: Coordinate) => void;
  onChangeVideoDuration?: (duration: number) => void;
  minPlayTime?: number;
  maxPlayTime?: number;
  textOverlays?: TextOverlay[];
  menus?: JSX.Element[];
  viewItems?: JSX.Element[];
  noFullscreen?: boolean;
  overridePanMethod?: PanMethod;
};

const playbackPopupTimeMs: number = 700;

const VideoLayout = (props: Props) => {
  const classes = useStyles();

  //props
  const {
    videos,
    showOverlayUI,
    saveId,
    defaultPanMethod,
    updateIntervalMs,
    videoPlaying,
    onChangeVideoPlaying,
    playTime,
    playTimeChanged,
    seekVideo,
    setSeekVideo,
    playbackSpeed,
    viewOverlays,
    mouseOverlay,
    onViewMouseDown,
    onViewMouseUp,
    onViewMouseMove,
    onChangeVideoDuration,
    minPlayTime,
    maxPlayTime,
    textOverlays,
    menus: externalMenus,
    viewItems: viewExtensionItems,
    noFullscreen,
    overridePanMethod,
  } = props;

  //state
  const [prevTime, setPrevTime] = useState<number>(-1);
  const [sharedDuration, setSharedDuration] = useState<number>(0);
  const [videoPlayingStates] = useState<Map<string, boolean>>(new Map<string, boolean>());
  const [videoLoading] = useState<Map<string, boolean>>(new Map<string, boolean>());
  const [delayTimes] = useState<Map<string, number>>(new Map<string, number>());
  const [updateVideos, setUpdateVideos] = useState<boolean>(false);
  const [videoSyncRequest, setVideoSyncRequest] = useState<boolean>(false);
  const [wasPlaying, setWasPlaying] = useState<boolean>(false);
  const [sharedZoom, setSharedZoom] = useState<number>(1);
  const [rightClickInfo, setRightClickInfo] = useState<ClickInfo | undefined>(undefined);
  const [maxSyncOffsetSeconds, setMaxSyncOffsetSeconds] = useState<number>(0);
  const [activeView, setActiveView] = useState<Video | undefined>(undefined);
  const [prevFullscreen, setPrevFullscreen] = useState<boolean | undefined>(undefined);
  const [changeViewState, setChangeViewState] = useState<ReactZoomPanPinchState | undefined>(undefined);
  const [prevPlaybackSpeed, setPrevPlaybackSpeed] = useState<number | undefined>(playbackSpeed);
  const [popupMessage, setPopupMessage] = useState<PopupMessage | undefined>(undefined);

  // Menu options
  const [storedFullscreen, setStoredFullscreen] = useLocalStorage<boolean>((saveId || '') + '_Fullscreen', true);

  const [panMethod, setPanMethod] = useLocalStorage<PanMethod>(
    (saveId || '') + '_PanMethod',
    defaultPanMethod || PanMethod.Drag
  );

  const fullscreen: boolean = noFullscreen ? false : storedFullscreen;
  const fps: number = (videos && videos.length > 0 && videos[0].fps) || 25;

  //refs
  const divRef = React.useRef<HTMLDivElement>(null);

  //hard-set values right now
  const viewHorizontalPadding: number = 0;

  //get the correct view sizes
  const { width: fullWidth, height: viewHeight } = useContainerDimensions(divRef);
  const viewWidth: number = fullWidth / videos.length - viewHorizontalPadding * 2;

  const getDelayAverage = useCallback(() => {
    if (activeView) {
      return delayTimes.get(String(activeView.id)) || 0;
    }

    const times: number[] = Array.from(delayTimes.values());
    return times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
  }, [activeView, delayTimes]);

  const updatePlayTime = useCallback(() => {
    // We only update play time whenever we received atleast 1 update from a child video.
    if (delayTimes.size === 0) {
      return;
    }

    // Get our average delay
    const delayAvg: number = getDelayAverage();
    // Now clear delaytimes again.
    delayTimes.clear();

    const now = Date.now();

    // Update popup message time & unset when time <= 0
    if (popupMessage?.time) {
      popupMessage.time -= now - prevTime;
      if (popupMessage.time <= 0) {
        setPopupMessage(undefined);
      }
    }

    // Only update if we have a previous time and are not seeking.
    if (prevTime !== -1 && !seekVideo && !videoSyncRequest) {
      const differenceTime: number = now - prevTime;
      const addTime: number = differenceTime * (playbackSpeed || 1);

      let newTime: number = playTime + addTime * 0.001 + delayAvg;

      //don't return new time if we surpass our boundaries ( if set )
      if (maxPlayTime && newTime > maxPlayTime) {
        onChangeVideoPlaying && onChangeVideoPlaying(false, 'Surpassed max playtime');
        newTime = maxPlayTime;
      } else if (newTime > sharedDuration) {
        onChangeVideoPlaying && onChangeVideoPlaying(false, 'Time above sharedDuration');
        newTime = sharedDuration;
      } else if (minPlayTime && newTime < minPlayTime) {
        onChangeVideoPlaying && onChangeVideoPlaying(false, 'Below our minimum playtime');
        newTime = minPlayTime;
      } else if (newTime < 0) {
        onChangeVideoPlaying && onChangeVideoPlaying(false, 'Time below 0');
        newTime = 0;
      }
      playTimeChanged && playTimeChanged(newTime);
    }

    // Set previous time.
    setPrevTime(videoPlaying ? now : -1);
  }, [
    delayTimes,
    getDelayAverage,
    popupMessage,
    prevTime,
    seekVideo,
    videoSyncRequest,
    videoPlaying,
    playbackSpeed,
    playTime,
    maxPlayTime,
    sharedDuration,
    minPlayTime,
    playTimeChanged,
    onChangeVideoPlaying,
  ]);

  const changePopupMessage = useCallback((value: PopupMessage | undefined) => {
    setPopupMessage((prev) => {
      if ((!value && prev) || (!prev && value) || (value && prev && prev.priority <= value.priority)) {
        return value;
      } else {
        return prev;
      }
    });
  }, []);

  const onVideoProgress = useCallback(
    (id: string, p: ProgressState) => {
      if (minPlayTime && p.playedSeconds <= minPlayTime) {
        return;
      }
      delayTimes.set(id, p.playedSeconds - playTime - maxSyncOffsetSeconds);
    },
    [delayTimes, maxSyncOffsetSeconds, minPlayTime, playTime]
  );

  const didAllVideosLoad = useCallback(() => {
    if (activeView) {
      return !videoLoading.get(String(activeView.id)) || false;
    }

    const bufferValues: boolean[] = Array.from(videoLoading.values());
    return bufferValues.every((val: boolean) => !val);
  }, [activeView, videoLoading]);

  const onVideoPlaying = useCallback(
    (id: string, isPlaying: boolean) => {
      if (!onChangeVideoPlaying || videoSyncRequest || seekVideo) {
        return;
      }

      videoPlayingStates.set(id, isPlaying);
    },
    [onChangeVideoPlaying, seekVideo, videoPlayingStates, videoSyncRequest]
  );

  const onVideoLoading = useCallback(
    (id: string, isLoading: boolean) => {
      videoLoading.set(id, isLoading);

      // Update state of all videos being loaded
      const allVideosLoaded: boolean = didAllVideosLoad();
      changePopupMessage(
        allVideosLoaded ? undefined : { value: (videoPlaying ? 'Playing' : 'Seeking') + ' video...', priority: 5 }
      );

      // Auto-play video after sync request
      if (allVideosLoaded && videoSyncRequest) {
        onChangeVideoPlaying &&
          videoPlaying !== wasPlaying &&
          onChangeVideoPlaying(wasPlaying, 'Play video after sync request');
        setVideoSyncRequest(false);
      }
    },
    [
      changePopupMessage,
      didAllVideosLoad,
      onChangeVideoPlaying,
      videoLoading,
      videoPlaying,
      videoSyncRequest,
      wasPlaying,
    ]
  );

  const resyncViews = useCallback(() => {
    setWasPlaying(videoPlaying === true);
    setVideoSyncRequest(true);
    onChangeVideoPlaying && onChangeVideoPlaying(false, 'Resync view pause');
  }, [onChangeVideoPlaying, videoPlaying]);

  // Shared zoom when autoswitch mode
  const onSharedZoomChange = useCallback((id: string, newZoom: number) => {
    setSharedZoom(newZoom);
  }, []);

  // Handle right click
  const handleRightClick = useCallback((info: ClickInfo) => {
    // Set right click info to pass down to our views, callback from onViewMouseDown
    setRightClickInfo(info);
  }, []);

  const handleRightClickRelease = useCallback(
    (info: ClickInfo) => {
      onViewMouseUp && onViewMouseUp('', info);
    },
    [onViewMouseUp]
  );

  const handleChangeVideoDuration = useCallback(
    (duration: number, syncOffset: number) => {
      //set video duration only from the video with the shortest duration
      const newSharedDuration = duration - (maxSyncOffsetSeconds - syncOffset);
      if (newSharedDuration < sharedDuration || sharedDuration === 0) {
        setSharedDuration(newSharedDuration);
      }
    },
    [maxSyncOffsetSeconds, sharedDuration]
  );

  const handleViewMouseMove = useCallback(
    (viewId: string, coord: Coordinate | undefined) => {
      if (!coord || !onViewMouseMove) {
        // Don't send mouse move updates upwards that are outside the view
        return;
      }
      onViewMouseMove(viewId, coord);
    },
    [onViewMouseMove]
  );

  // Tries to change view to the one on right
  const tryMoveVideoRight = useCallback(
    (viewState: ReactZoomPanPinchState | undefined = undefined) => {
      if (activeView) {
        const videoOrder: number = isNaN(Number(activeView?.sortOrder)) ? -1 : Number(activeView?.sortOrder);
        const nextOrderView: Video | undefined = videos.find((v: Video) => v.sortOrder === videoOrder + 1);
        if (nextOrderView) {
          console.log('[Switch View - Right] ', nextOrderView.id, viewState);
          setActiveView(nextOrderView);
          viewState && setChangeViewState(viewState);
          resyncViews();
        }
      } else {
        console.log('No valid current video on TryMoveVideoRight function');
      }
    },
    [activeView, resyncViews, videos]
  );

  // Tries to change view to the one on left
  const tryMoveVideoLeft = useCallback(
    (viewState: ReactZoomPanPinchState | undefined = undefined) => {
      if (activeView) {
        const viewOrder: number = isNaN(Number(activeView?.sortOrder)) ? -1 : Number(activeView?.sortOrder);
        const nextOrderView: Video | undefined = videos.find((v: Video) => v.sortOrder === viewOrder - 1);
        if (nextOrderView) {
          console.log('[Switch View - Left] ', nextOrderView.id, viewState);
          setActiveView(nextOrderView);
          viewState && setChangeViewState(viewState);
          resyncViews();
        }
      } else {
        console.log('No valid current video on TryMoveVideoLeft function');
      }
    },
    [activeView, resyncViews, videos]
  );

  // Set our active video to be the one best suitable given our circumstance
  const setBestCameraForPosition = useCallback(() => {
    if (videos.length === 0) {
      setStoredFullscreen(false);
      return;
    }

    // Lets get our best view
    let bestView: Video | undefined = videos[0];
    let bestResult: number = Number.MAX_VALUE;

    const allDistances: number[] = [];

    viewOverlays?.forEach((viewObjects: VideoOverlayObject[], key: string) => {
      const foundCenterObj: VideoOverlayObject | undefined = viewObjects.find(
        (obj: VideoOverlayObject) => obj.centerViewOnObject
      );
      const video = videos.find((v: Video) => String(v.id) === key);
      if (!video?.width || !video?.height || !foundCenterObj) return;

      const dX: number = foundCenterObj.center.x - video.width * 0.5;
      const dY: number = foundCenterObj.center.y - video.height * 0.5;
      const distResult: number = Math.hypot(dX, dY);

      allDistances.push(distResult);

      if (distResult < bestResult) {
        bestResult = distResult;
        bestView = video;
      }
    });

    console.log('[Switch View To Best Fit] ', bestView);
    setActiveView(bestView);
  }, [setStoredFullscreen, videos, viewOverlays]);

  // Update play time every updateIntervalMs (or 25 fps default)
  useInterval(() => {
    updatePlayTime();
  }, updateIntervalMs || 40);

  // Handle seek
  useEffect(() => {
    if (seekVideo) {
      onChangeVideoPlaying && onChangeVideoPlaying(false, 'Seek video pause');
      setSeekVideo && setSeekVideo(false);
    }
  }, [onChangeVideoPlaying, seekVideo, setSeekVideo]);

  // Handle fullscreen switch
  useEffect(() => {
    if (prevFullscreen === fullscreen) return;
    if (!fullscreen) {
      setActiveView(undefined);
      resyncViews();
    } else {
      setBestCameraForPosition();
    }
    setPrevFullscreen(fullscreen);
  }, [fullscreen, prevFullscreen, resyncViews, setBestCameraForPosition]);

  // Sync when pausing
  useEffect(() => {
    const update: boolean = !videoPlaying && playTime !== prevTime;
    setUpdateVideos(update);
  }, [videoPlaying, playTime, prevTime]);

  // When right click is set, undo it again (trigger once in child views)
  useEffect(() => {
    if (rightClickInfo) {
      setRightClickInfo(undefined);
    }
  }, [onViewMouseUp, rightClickInfo]);

  useEffect(() => {
    onChangeVideoDuration && onChangeVideoDuration(sharedDuration);
  }, [onChangeVideoDuration, sharedDuration]);

  // Set the maximum sync offset
  useEffect(() => {
    const sortVidsBySyncOffset = videos.slice().sort((a, b) => {
      return syncOffsetMs(b) - syncOffsetMs(a);
    });

    const maxOffset: number | undefined =
      sortVidsBySyncOffset.length > 0 ? syncOffsetMs(sortVidsBySyncOffset[0]) || undefined : undefined;
    setMaxSyncOffsetSeconds(maxOffset ? maxOffset * 0.001 : 0);
  }, [videos]);

  // Set pop up message for playbackspeed
  useEffect(() => {
    if (prevPlaybackSpeed !== playbackSpeed) {
      changePopupMessage({ value: '' + playbackSpeed + 'x', time: playbackPopupTimeMs, priority: 1 });
      setPrevPlaybackSpeed(playbackSpeed);
    }
  }, [changePopupMessage, playbackSpeed, prevPlaybackSpeed]);

  // Catch right click events
  useClickHandler({
    mouseButton: MouseButton.RightMouseButton,
    enabled: true,
    onClick: handleRightClick,
    onRelease: handleRightClickRelease,
  });

  /*
    Get our video view components
  */
  const useVideos = activeView !== undefined ? [activeView] : videos;

  const videoViews = useVideos
    .slice()
    .sort((a: Video, b: Video) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((video: Video) => {
      const videoSyncOffsetSeconds: number = syncOffsetMs(video) * 0.001;
      const videoPlayTime: number = playTime - videoSyncOffsetSeconds;
      const videoOrder: number = isNaN(Number(video?.sortOrder)) ? -1 : Number(video?.sortOrder);
      const hasVideoLeft: boolean =
        activeView !== undefined && videos.find((v: Video) => v.sortOrder === videoOrder - 1) !== undefined;
      const hasVideoRight: boolean =
        activeView !== undefined && videos.find((v: Video) => v.sortOrder === videoOrder + 1) !== undefined;

      return (
        <span
          key={video.id}
          style={{
            margin: '0px ' + viewHorizontalPadding + 'px',
          }}
        >
          <VideoView
            id={String(video.id)}
            viewWidth={fullscreen ? fullWidth : viewWidth}
            viewHeight={viewHeight}
            videoWidth={video?.width || 0}
            videoHeight={video?.height || 0}
            url={video.transcodeStatus?.streamUrl || ''}
            isPlaying={videoPlaying || false}
            playTime={maxSyncOffsetSeconds + videoPlayTime}
            syncOffset={videoSyncOffsetSeconds}
            updatePlayTime={updateVideos}
            playbackSpeed={playbackSpeed}
            overlayItems={viewOverlays?.get(String(video.id)) || []}
            mouseOverlay={mouseOverlay}
            onMouseDown={onViewMouseDown}
            onMouseUp={onViewMouseUp}
            onMouseMove={handleViewMouseMove}
            onChangeVideoDuration={handleChangeVideoDuration}
            intervalMs={updateIntervalMs}
            onVideoProgress={onVideoProgress}
            isFullscreen={fullscreen}
            sharedZoom={fullscreen ? sharedZoom : undefined}
            onSharedZoomChange={onSharedZoomChange}
            onChangeVideoPlaying={onVideoPlaying}
            onChangeVideoLoading={onVideoLoading}
            rightClickInfo={rightClickInfo}
            transformStateOverride={changeViewState}
            tryMoveVideoRight={hasVideoRight ? tryMoveVideoRight : undefined}
            tryMoveVideoLeft={hasVideoLeft ? tryMoveVideoLeft : undefined}
            videoSyncRequest={videoSyncRequest}
            panMethod={overridePanMethod !== undefined ? overridePanMethod : panMethod}
          />
        </span>
      );
    });

  let ti: number = 0;
  const overlayTexts: JSX.Element[] | undefined = textOverlays
    ?.filter((t: TextOverlay) => !t.frame_id || t.frame_id === Math.round(playTime * fps))
    .map((t: TextOverlay) => {
      return (
        <div
          key={ti++}
          style={{
            backgroundColor: t.background_color || '#0009',
            color: t.color || '#fff',
            padding: '2px 6px',
          }}
        >
          {t.value}
        </div>
      );
    });

  const viewMenuItems: JSX.Element[] = useMemo(() => {
    const videoOrder = isNaN(Number(activeView?.sortOrder)) ? -1 : Number(activeView?.sortOrder);
    const hasVideoLeft: boolean =
      activeView !== undefined && videos.find((v: Video) => v.sortOrder === videoOrder - 1) !== undefined;
    const hasVideoRight: boolean =
      activeView !== undefined && videos.find((v: Video) => v.sortOrder === videoOrder + 1) !== undefined;

    return [
      <ToggleMenuItem
        value={fullscreen}
        title="Fullscreen"
        shortcut="Shift+F"
        onChange={(value: boolean) => setStoredFullscreen(value)}
      />,
      <ToggleMenuItem
        value={panMethod === PanMethod.Drag}
        title="Drag Panning"
        shortcut="Shift+Q"
        onChange={(value: boolean) => {
          if (value) {
            setPanMethod(PanMethod.Drag);
          }
        }}
        disabled={panMethod === PanMethod.Drag}
      />,
      <ToggleMenuItem
        value={panMethod === PanMethod.Center}
        title="Focus View"
        shortcut="Shift+W"
        onChange={(value: boolean) => {
          setPanMethod(value ? PanMethod.Center : PanMethod.Drag);
        }}
      />,
      <ToggleMenuItem
        value={panMethod === PanMethod.Edge}
        title="Edge Panning"
        shortcut="Shift+E"
        onChange={(value: boolean) => {
          setPanMethod(value ? PanMethod.Edge : PanMethod.Drag);
        }}
      />,
      <ActionableMenuItem
        icon={<NavigateLeftIcon />}
        title="Switch View"
        shortcut="Shift+A"
        onTrigger={() => {
          tryMoveVideoLeft();
          // Manual switching disables auto-modes
          setPanMethod(PanMethod.Drag);
        }}
        disabled={!hasVideoLeft}
      />,
      <ActionableMenuItem
        icon={<NavigateRightIcon />}
        title="Switch View"
        shortcut="Shift+S"
        onTrigger={() => {
          tryMoveVideoRight();
          // Manual switching disables auto-modes
          setPanMethod(PanMethod.Drag);
        }}
        disabled={!hasVideoRight}
      />,
      ...(viewExtensionItems || []),
    ];
  }, [
    activeView,
    fullscreen,
    panMethod,
    setPanMethod,
    setStoredFullscreen,
    tryMoveVideoLeft,
    tryMoveVideoRight,
    videos,
    viewExtensionItems,
  ]);

  return (
    <>
      <div ref={divRef} className={classes.container}>
        {videoViews}
        {showOverlayUI && (
          <>
            <div className={classes.bottomTextOverlay}>{overlayTexts}</div>
            <div className={classes.menuContainer}>
              <Menu key="view" title="View" items={viewMenuItems} />
              {externalMenus}
            </div>
          </>
        )}
        {popupMessage && (!popupMessage?.time || popupMessage.time > 0) && (
          <div className={classes.centerTextOverlay}>{popupMessage?.value}</div>
        )}
      </div>
    </>
  );
};

export default VideoLayout;
